/*
  Copyright 2018-2020 National Geographic Society

  Use of this software does not constitute endorsement by National Geographic
  Society (NGS). The NGS name and NGS logo may not be used for any purpose without
  written permission from NGS.

  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/
import { Typography } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Popper from '@material-ui/core/Popper';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import concat from 'lodash/concat';
import compact from 'lodash/compact';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons as VizzIcons } from 'vizzuality-components';
import { replace } from 'redux-first-router';
import { isSuperAdmin, ErrorBoundary, QUERIES } from '@marapp/earth-shared';

import { bbox, center } from '@turf/turf';

import { useAuth0 } from '../../auth/auth0';
import CollectionNew from '../../components/collection/collection-new';
import CollectionsCard from '../../components/collection/collections-card';
import Header from '../../components/header';
import LastViewedPlace from '../../components/last-viewed-place';
import LayerConfigError from '../../components/layer-config-error';
import Layers from '../../components/layers';
import Map from '../../components/map';
import Places from '../../components/places';
import FeaturedPlaces from '../../components/places/featured-places';
import Sidebar from '../../components/sidebar';
import Url from '../../components/url';
import PlacesService from '../../services/PlacesService';
import {
  MAP_ENABLE_ELSA,
  MAP_SIDEBAR_WIDTH,
  MAP_SHOW_DISCLAIMER,
  AI_API_URL,
  AI_API_KEY,
} from '../../config';
import { useLayers } from '../../fetchers';
import { ILastViewedPlace, INotification } from '../../modules/global/model';
import { EarthRoutes } from '../../modules/router/model';
import { EPanels } from '../../modules/sidebar/model';
import CollectionDetails from './collection-details';
import PlaceDetails from './place-details';
import { URL_PROPS } from './url';
import { PreviewLayer } from '../../utils/preview';

import IOChat from '../../components/chat';

import interceptClicks from 'intercept-link-clicks';
import { update } from 'lodash';
import { setMapStyle } from '../../modules/map/actions';
import { MapAction } from 'components/chat/langchain/langgraph/types/adapterOptions';

const useStyles = makeStyles((theme) => {
  const minTabHeight = theme.spacing(4.5);

  return {
    root: {
      background: theme.palette.grey['900'],
      flex: 1,
      position: 'relative',
      width: '100%',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
    },
    content: {
      position: 'relative',
      width: '100%',
      height: '100vh',
      zIndez: 0,
    },
    mapContainer: (props: any) => ({
      height: props.showExpansionContent ? 'calc(100vh - 64px)' : '100%',
      width: '100%',
      transition: theme.transitions.create('height', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
    tabContainer: {
      backgroundColor: theme.palette.grey['600'],
    },
    tabs: {
      minHeight: minTabHeight,
      '& button': {
        minWidth: 0,
        paddingLeft: theme.spacing(2.5),
        paddingRight: theme.spacing(2.5),
        minHeight: minTabHeight,
      },
    },
    tooltip: {
      background: '#616161',
      opacity: '90%',
      borderRadius: 4,
      padding: '4px 8px',
      cursor: 'pointer',
    },
  };
});

interface IProps {
  resetLayers?: Function;
  setLayersActive?: (payload: any) => void;
  setLayersActiveWithOpacity?: (payload: any) => void;
  setLocationHighlight?: (payload: any) => void;
  setMapBounds?: (payload: any) => void;
  setMapViewport?: (payload: any) => void;
  setNotification?: (payload: any) => void;
  setPlacesSearch?: (payload: any) => void;
  setPreview?: (payload: any) => void;
  setSidebarPanel?: (payload: any) => void;
  setSidebarPanelExpanded?: (payload: any) => void;
  setSidebarOpen?: (o: boolean) => void;
  setWorkspaces?: (payload: any) => void;
  setLayersSearch?: (payload: any) => void; // Add this line if not present
  resetMap?: Function; // Add this line if not present
  bounds?: object;
  sidebar: {
    open: boolean;
    panel?: EPanels;
    panelExpanded: boolean;
  };
  page?: string;
  notification?: INotification;
  mapStyle?: string;
  layers?: any;
  layersPanel?: boolean;
  lastViewedPlace?: ILastViewedPlace;
  latestResult?: string;
  selected?: string;
  collection?: any;
  router?: any;
}

const { EARTH, COLLECTION, LOCATION, NEW_COLLECTION } = EarthRoutes;

const EarthPage = (props: IProps) => {
  const {
    resetLayers,
    setLayersActive,
    setLayersActiveWithOpacity,
    setLocationHighlight,
    setMapBounds,
    setMapViewport,
    setNotification,
    setPlacesSearch,
    setPreview,
    setSidebarPanel,
    setSidebarPanelExpanded,
    setSidebarOpen,
    setWorkspaces,
    setLayersSearch, // Add this line
    sidebar: { panel, open: sidebarOpen },
    notification,
    router,
    lastViewedPlace,
    latestResult,
    layers,
    mapStyle,
    resetMap,
  } = props;
  const { type } = router;
  const { i18n, t } = useTranslation();
  const { userData, getAccessToken, privateGroups, selectedGroup, isAuthenticated } = useAuth0();
  const [token, setToken] = useState('');
  const [selectedThread, setSelectedThread] = useState('');
  const selectedThreadRef = useRef(selectedThread);
  const [threadMap, setThreadMap] = useState<{ [threadId: string]: URL }>({});
  const [threads, setThreads] = useState([]);
  const [hasAutoSelectedThread, setHasAutoSelectedThread] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [isMapReloading, setMapReloading] = useState(false);
  const [saveThreadTimeout, setSaveThreadTimeout] = useState<NodeJS.Timeout | null>(null);
  const { data: activeLayers } = useLayers(QUERIES.LAYER.getActive(layers.active.join(';')));
  // const { data: groupsWithELSA } = useAnalyses(
  //   QUERIES.ANALYSIS.getAllFiltered(selectedGroup.join(','), 'ELSA')
  // );
  // const { data: fetchedResult } = useAnalysisResult(
  //   latestResult,
  //   QUERIES.ANALYSIS.getOne(selectedGroup.join(','))
  // );

  const theme = useTheme();
  const selectedOpen = [LOCATION, COLLECTION, NEW_COLLECTION].includes(type);
  const withHeaderLayout = [EARTH, LOCATION, COLLECTION].includes(type);
  const newCollectionLayout = [NEW_COLLECTION].includes(type);
  const canCreateCollections = !!privateGroups.length;
  const showLastViewedPlace =
    lastViewedPlace && selectedGroup.includes(lastViewedPlace.organization);
  // const showELSA = MAP_ENABLE_ELSA && !!groupsWithELSA?.length;

  const isSmallDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const showExpansionContent = type === LOCATION && isSmallDevice;
  const classes = useStyles({ ...props, showExpansionContent });

  useEffect(() => {
    async function fetchToken() {
      const accessToken = await getAccessToken();
      setToken(() => accessToken);
    }
    if (isAuthenticated) {
      fetchToken();
    }
  }, [isAuthenticated, getAccessToken]);

  useEffect(() => {
    if (!isChatModalOpen) {
      // Skip the fetch if the chat modal is not open
      return;
    }

    fetch(`${AI_API_URL}/threads/search`, {
      method: 'POST',
      body: JSON.stringify({
        metadata: { user_id: userData.email },
        limit: 10,
        offset: 0,
        sort_by: 'updated_at',
        sort_order: 'desc',
      }),
    })
      .then((response) => {
        if (!response.ok) {
          // Log an error if the response status is not OK
          console.error(`Failed to fetch threads. Status: ${response.status}`);
          return null; // Return null to indicate failure
        }

        return response.json();
      })
      .then((data) => {
        if (!data) {
          // Handle the case where no data is returned
          console.warn('No data returned from threads API.');
          return;
        }

        const fetchedThreads = data.map((thread) => ({
          thread_id: thread.thread_id,
          created_at: thread.created_at,
          updated_at: thread.updated_at,
          href: thread.metadata.href,
        }));

        setThreads(fetchedThreads);

        // Auto-select the most recent thread on first load
        if (!hasAutoSelectedThread && fetchedThreads.length > 0 && !selectedThread) {
          const mostRecentThread = fetchedThreads[0];
          setSelectedThread(mostRecentThread.thread_id);
          setHasAutoSelectedThread(true);
        }
      })
      .catch((error) => {
        // Log any network or runtime errors
        console.error('Error fetching threads:', error);
      });
  }, [userData, selectedThread, isChatModalOpen]);

  useEffect(() => {
    if (!selectedThread) {
      return;
    }

    // Check if current URL already has map state - if so, don't override it
    const currentParams = new URLSearchParams(window.location.search);
    const hasMapState =
      currentParams.has('layers') ||
      currentParams.has('coordinates') ||
      currentParams.has('basemap') ||
      currentParams.has('geojson');

    if (hasMapState) {
      console.log('ℹ️ URL already contains map state, skipping thread auto-restore');
      return;
    }

    // Check if we already have the href in the threads list
    const threadData = threads.find((t) => t.thread_id === selectedThread);
    if (threadData?.href) {
      console.log('🔄 Auto-restoring map state from thread href:', threadData.href);
      try {
        const url = new URL(threadData.href);
        // Don't update thread href when auto-restoring on load
        handleClick(url, false);
      } catch (error) {
        console.error('Error parsing thread href:', error);
      }
      return;
    }

    // Otherwise fetch thread data from API
    fetch(`${AI_API_URL}/threads/${selectedThread}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          console.error('Failed to retrieve thread');
        }
      })
      .then((data) => {
        if (data?.metadata?.href) {
          console.log('🔄 Restoring map state from fetched thread data:', data.metadata.href);
          try {
            const url = new URL(data.metadata.href);
            handleClick(url, false);
          } catch (error) {
            console.error('Error parsing thread href:', error);
          }
        }
      })
      .catch((err) => console.error(err));
  }, [selectedThread, threads]);

  useEffect(() => {
    selectedThreadRef.current = selectedThread;
  }, [selectedThread]);

  // Auto-save thread href whenever URL changes (debounced)
  useEffect(() => {
    if (!selectedThread) {
      return;
    }

    // Clear any existing timeout
    if (saveThreadTimeout) {
      clearTimeout(saveThreadTimeout);
    }

    // Debounce the save to avoid spamming the API during rapid URL changes
    const timeout = setTimeout(() => {
      console.log('💾 Auto-saving thread href due to URL change');
      handleSaveThreadHref(selectedThread);
    }, 1000); // Wait 1 second after last change

    setSaveThreadTimeout(timeout);

    // Cleanup timeout on unmount
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [window.location.href, selectedThread]); // Trigger on URL or thread change

  // Custom watcher that monitors changes in it's dependency list
  // Each time such a dependency changes, a new MapBox instance will be created
  useEffect(() => {
    setMapReloading(true);
    // List of dependencies that trigger a new instance of the map (map reload)
  }, [mapStyle, i18n.language]);

  // 'Workaround flag' - dictates a new render cycle with the new MapBox instance
  useEffect(() => {
    isMapReloading && setMapReloading(false);
  }, [isMapReloading]);

  useEffect(() => {
    setSidebarOpen(!isSmallDevice);
  }, [isSmallDevice]);

  useEffect(() => {
    const shouldCloseSidenav = isSmallDevice && (type === LOCATION || type === EARTH);

    if (shouldCloseSidenav) {
      setSidebarOpen(false);
    }
  }, [type, isSmallDevice]);

  useEffect(() => {
    // reset sidebar panel to first of possible choices when page type changes
    if (!Object.values(EPanels).includes(panel)) {
      setSidebarPanel(EPanels.PLACES);
    }
  }, [type]);

  // useEffect(() => {
  //   if (!fetchedResult) {
  //     return;
  //   }
  //   if (
  //     fetchedResult.status === AnalysisResultStatusEnum.COMPLETE &&
  //     fetchedResult.type === AnalysisTypeEnum.ELSA
  //   ) {
  //     setNotification(t('Your ELSA Map is ready'));
  //   }
  // }, [fetchedResult]);

  // create temporary preview layer
  let previewLayer;
  if (layers.preview) {
    previewLayer = PreviewLayer(layers.preview);
    layers.active = [previewLayer.slug];
  }

  function updateThread(threadId: string, payload: object) {
    fetch(`${AI_API_URL}/threads/${threadId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (response.ok) {
          console.log('Thread updated:', threadId);
        } else {
          console.error('Failed to update thread:', threadId);
        }
      })
      .catch((err) => console.error(err));
  }

  const handleSaveThreadHref = useCallback((threadId) => {
    if (!threadId) {
      console.error('No threadId provided to handleSaveThreadHref');
      return;
    }

    updateThread(threadId, {
      metadata: {
        href: window.location.href,
      },
    });
  }, []); // No dependencies needed since we use window.location.href directly

  const handleThreadChange = (event) => {
    console.log('Thread selected: ', event.target.value);
    setSelectedThread(event.target.value);
    updateThread(event.target.value, { metadata: { last_viewed: new Date().toISOString() } });
  };

  /* Handle map actions returned from agent in state.
   */
  const handleMapActions = useCallback(
    async (actions: MapAction[]) => {
      // ! MapAction payloads defined in white-rabbit
      for (const mapAction of actions) {
        // ! Note: URL sync is now handled by <Url> component
        if (mapAction.type === 'setLayersActive') {
          const layerSettings = mapAction.parameters;
          setLayersActiveWithOpacity({
            active: Object.keys(layerSettings),
            settings: layerSettings,
          });
        } else if (mapAction.type === 'setLocation') {
          // TODO: figure out how to use actions to set location highlight, rather than redux-router
          let path = mapAction.parameters.path;

          // ensure leading slash
          if (!path.startsWith('/')) {
            path = '/' + path;
          }
          // replaces URL path (requires leading slash to fully replace path)
          replace(path);
        }
      }
    },
    [setLayersActive, setMapViewport]
  );

  const handleClick = (url: URL, updateThreadHref: boolean = false) => {
    if (url) {
      console.log('🔄 handleClick: Restoring map state from URL:', url.href);
      const currentPath = window.location.pathname;

      if (url.host === window.location.host) {
        // Navigate to earth if on different path
        if (url.pathname !== currentPath) {
          replace('/earth');
        }

        // Use the original search string directly to avoid double encoding
        const newSearch = url.search;
        const params = Object.fromEntries(new URLSearchParams(newSearch));

        console.log('  📋 URL params:', params);

        // Handle layers
        if ('layers' in params) {
          console.log('  🗺️ Restoring layers:', params.layers);
          const layerOpacities = (params['layers'] || '').split(',').map((str) => {
            const slug = str.replace(/_\d{1,3}$/, '');
            const match = str.match(/_(\d{1,3})$/);
            const opacity = match ? Number(match[1]) / 100 : 1;
            return { name: slug, opacity };
          });
          //console.log('DEBUG layerOpacities:', layerOpacities);

          setLayersActiveWithOpacity({
            active: layerOpacities.map(({ name }) => name),
            settings: Object.fromEntries(
              layerOpacities.map(({ name, opacity }) => [name, { opacity }])
            ),
          });
        } else {
          resetLayers();
        }

        // Handle coordinates
        if ('coordinates' in params) {
          const coordinates = params['coordinates'].split(',');
          let latitude, longitude, zoom;
          if (coordinates.some((val) => isNaN(Number(val)))) {
            // defense: if NaNs, reset center and zoom
            [latitude, longitude, zoom] = [20, 0, 2];
            console.warn('  ⚠️ Invalid coordinates, using defaults');
          } else {
            [latitude, longitude, zoom] = coordinates.map(Number);
          }
          const viewport = { longitude, latitude, zoom };
          console.log('  📍 Restoring viewport:', viewport);
          setMapViewport(viewport);
        } else {
          console.log('  ℹ️ No coordinates in URL');
        }

        // Handle basemap
        if ('basemap' in params) {
          console.log('  🗺️ Restoring basemap:', params.basemap);
          setMapStyle(params['basemap']);
        }

        // Handle geojson
        if ('geojson' in params) {
          console.log('  📍 Restoring geojson highlight');
          const geojson = JSON.parse(atob(params['geojson']));
          const payload = { id: 'temp', geojson: geojson };
          setMapBounds({ bbox: bbox(geojson) });
          setLocationHighlight(payload);
        }

        // Update URL - use original search string
        const finalUrl = `${url.pathname}${newSearch}`;
        console.log('  🔄 Updating browser URL to:', finalUrl);
        replace(finalUrl);

        // Note: Thread href will be auto-saved by URL change effect

        console.log('✅ handleClick: Map state restoration complete');
      } else {
        window.open(url, '_blank');
      }
    }
  };

  interceptClicks(
    {
      sameOrigin: false,
    },
    (e, el) => {
      try {
        e.preventDefault();
        const url = new URL(el.href);
        console.log(`Selected thread: ${selectedThread}`);
        handleClick(url); // Removed updateThreadHref parameter - now auto-saved
      } catch (error) {
        console.error(error);
      }
    }
  );

  // Expose handleClick globally for IOChat to access
  useEffect(() => {
    (window as any).earthPageHandleClick = handleClick;

    return () => {
      delete (window as any).earthPageHandleClick;
    };
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <div className={classes.mapContainer}>
          <Popper
            className={classes.tooltip}
            open={!!notification}
            onClick={() => {
              setNotification(false);
            }}
            style={{
              left: (sidebarOpen ? MAP_SIDEBAR_WIDTH : 0) + 40,
              top: 16,
            }}
          >
            <Typography variant="body2">{notification}</Typography>
          </Popper>
          <ErrorBoundary fallbackComponent={<LayerConfigError selectedOpen={selectedOpen} />}>
            {!isMapReloading && (
              <Map
                page={props.page}
                selectedOpen={selectedOpen}
                t={t}
                activeLayers={
                  previewLayer ? compact(concat(activeLayers, previewLayer)) : activeLayers
                }
                showDisclaimerDefault={MAP_SHOW_DISCLAIMER}
              />
            )}
          </ErrorBoundary>
        </div>

        <Sidebar selectedOpen={selectedOpen}>
          {withHeaderLayout && (
            <div className="sidebar-content-wrapper">
              {/* Fixed header */}
              <div className="sidebar-header-fixed">
                <Header
                  threads={threads}
                  setThreads={setThreads}
                  selectedThread={selectedThread}
                  handleThreadChange={handleThreadChange}
                  handleClick={handleClick}
                  resetMap={resetMap}
                  setPlacesSearch={setPlacesSearch}
                  setWorkspaces={setWorkspaces}
                  setLayersSearch={setLayersSearch}
                  setSidebarPanel={setSidebarPanel}
                  resetLayers={resetLayers}
                  setNotification={setNotification}
                />
              </div>

              {/* Scrollable content */}
              <div className="sidebar-content-scrollable">
                <IOChat
                  selectedThread={selectedThread}
                  baseUrl={AI_API_URL}
                  userData={userData}
                  token={token}
                  handleSaveThreadHref={handleSaveThreadHref}
                  handleClick={handleClick} // Pass handleClick as prop
                  handleMapActions={handleMapActions}
                />

                <div>
                  <div
                    style={
                      isSuperAdmin(userData.roles as any)
                        ? { display: 'unset' }
                        : { display: 'none' }
                    }
                  >
                    <Box px={2} className={classes.tabContainer}>
                      <Tabs
                        className={classes.tabs}
                        textColor="primary"
                        value={panel}
                        onChange={(_, newValue) => setSidebarPanel(newValue)}
                      >
                        {[EARTH, LOCATION, COLLECTION].includes(type) && (
                          <Tab
                            label={t('Places')}
                            value={EPanels.PLACES}
                            className="marapp-qa-places-tab"
                          />
                        )}
                        {[EARTH, LOCATION, COLLECTION].includes(type) && (
                          <Tab
                            label={t('Layers')}
                            value={EPanels.LAYERS}
                            className="marapp-qa-layers-tab"
                          />
                        )}
                      </Tabs>
                    </Box>

                    {type === EARTH && (
                      <>
                        {panel === EPanels.PLACES && (
                          <Places selected={selectedOpen}>
                            <>
                              {showLastViewedPlace && (
                                <LastViewedPlace place={lastViewedPlace} group={selectedGroup} />
                              )}
                              <CollectionsCard
                                group={selectedGroup}
                                canCreate={canCreateCollections}
                              />
                              <FeaturedPlaces
                                group={selectedGroup}
                                setPlacesSearch={setPlacesSearch}
                                setSidebarPanelExpanded={setSidebarPanelExpanded}
                              />
                            </>
                          </Places>
                        )}
                        {/* {panel === EPanels.LAYERS && (
                          <Layers selected={selectedOpen} preview={previewLayer}>
                            {showELSA && (
                              <ELSACard
                                group={selectedGroup}
                                canCreate={showELSA} // TODO, should this be limited to users with a CREATE_ELSA privilege?
                                layers={layers}
                                setMapLayer={setPreview}
                              />
                            )}
                          </Layers>
                        )} */}
                      </>
                    )}

                    {type === LOCATION && (
                      <>
                        {panel === EPanels.CHAT && <></>}
                        {panel === EPanels.PLACES && (
                          <PlaceDetails panel={panel} selected={selectedOpen} />
                        )}
                        {panel === EPanels.LAYERS && (
                          <Layers selected={selectedOpen} preview={previewLayer} />
                        )}
                      </>
                    )}

                    {type === COLLECTION && (
                      <CollectionDetails panel={panel} selected={selectedOpen} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {newCollectionLayout && <CollectionNew privateGroups={privateGroups} router={router} />}
        </Sidebar>
      </div>
      {/* Bidirectional URL ↔ Redux sync for query params */}
      <Url urlProps={URL_PROPS} />
    </div>
  );
};

export default EarthPage;
